import React from "react";
import { useTranslation } from "react-i18next";
import type { AdminDashboardKpis } from "../../../graphql/types";
import { formatNumberDe, formatPercentDe } from "../../../utils/format";
import "./KpiGrid.css";

interface KpiGridProps {
  data: AdminDashboardKpis | null;
  loading: boolean;
  /** Short range label for the "Neue User · {range}" card (Tag/Woche/Monat or custom span). */
  rangeShort: string;
}

/**
 * The 4 dashboard KPI cards (§6.4 FE-2): total users, new users + trend pill,
 * active users, and the subscription breakdown. Shows shimmer skeletons while
 * the first load is in flight; keeps values during a range refetch.
 */
export const KpiGrid: React.FC<KpiGridProps> = ({ data, loading, rangeShort }) => {
  const { t } = useTranslation();

  if (!data && loading) {
    return (
      <div className="kpi-grid">
        {[0, 1, 2, 3].map((i) => (
          <div className="kpi-card" key={i}>
            <div className="lyra-skeleton kpi-skeleton-label" />
            <div className="lyra-skeleton kpi-skeleton-value" />
            <div className="lyra-skeleton kpi-skeleton-caption" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const trendUp = data.newUsersTrendPct >= 0;

  return (
    <div className="kpi-grid">
      {/* 1 · Total users */}
      <div className="kpi-card">
        <div className="kpi-head">
          <span className="kpi-dot kpi-dot--accent" />
          <span className="kpi-label">{t("dashboard.kpi.totalUsers")}</span>
        </div>
        <div className="kpi-value">{formatNumberDe(data.totalUsers)}</div>
        <div className="kpi-caption">{t("dashboard.kpi.totalUsersCaption")}</div>
      </div>

      {/* 2 · New users + trend */}
      <div className="kpi-card">
        <div className="kpi-head">
          <span className="kpi-dot kpi-dot--accent" />
          <span className="kpi-label">
            {t("dashboard.kpi.newUsers")} · {rangeShort}
          </span>
        </div>
        <div className="kpi-value-row">
          <span className="kpi-value">{formatNumberDe(data.newUsers)}</span>
          <span className={`kpi-trend${trendUp ? " kpi-trend--up" : " kpi-trend--down"}`}>
            {trendUp ? "▲" : "▼"} {formatPercentDe(Math.abs(data.newUsersTrendPct))} %
          </span>
        </div>
        <div className="kpi-caption">{t("dashboard.kpi.newUsersCaption")}</div>
      </div>

      {/* 3 · Active users */}
      <div className="kpi-card">
        <div className="kpi-head">
          <span className="kpi-dot kpi-dot--warm" />
          <span className="kpi-label">{t("dashboard.kpi.activeUsers")}</span>
        </div>
        <div className="kpi-value">{formatNumberDe(data.activeUsers30d)}</div>
        <div className="kpi-caption">{t("dashboard.kpi.activeUsersCaption")}</div>
      </div>

      {/* 4 · Subscription breakdown */}
      <div className="kpi-card">
        <div className="kpi-head">
          <span className="kpi-dot kpi-dot--accent" />
          <span className="kpi-label">{t("dashboard.kpi.subStatus")}</span>
        </div>
        <div className="kpi-subs">
          <div className="kpi-sub">
            <span className="kpi-sub-value kpi-sub-value--trial">
              {formatNumberDe(data.subscriptions.trial)}
            </span>
            <span className="kpi-sub-label">{t("dashboard.kpi.subTrial")}</span>
          </div>
          <div className="kpi-sub">
            <span className="kpi-sub-value">{formatNumberDe(data.subscriptions.active)}</span>
            <span className="kpi-sub-label">{t("dashboard.kpi.subActive")}</span>
          </div>
          <div className="kpi-sub">
            <span className="kpi-sub-value kpi-sub-value--free">
              {formatNumberDe(data.subscriptions.free)}
            </span>
            <span className="kpi-sub-label">{t("dashboard.kpi.subFree")}</span>
          </div>
          <div className="kpi-sub">
            <span className="kpi-sub-value kpi-sub-value--expired">
              {formatNumberDe(data.subscriptions.expired)}
            </span>
            <span className="kpi-sub-label">{t("dashboard.kpi.subExpired")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
